/**
 * Mapeo de rutas del sistema a features del plan de negocio
 *
 * Cada ruta protegida debe estar mapeada a su correspondiente feature del plan.
 * Los features son los mismos IDs definidos en src/utils/paths.ts
 *
 * IMPORTANTE: Los features que vienen del backend son los mismos que los IDs de paths.ts
 * Ejemplo: "catalog/categories", "inventory/inventories", etc.
 */

export const ROUTE_TO_FEATURE_MAP: Record<string, string> = {
  // ===== AUTH =====
  "/usuarios": "auth/users",

  // ===== REPORTES =====
  "/reportes": "report/reports",

  // ===== INVENTARIO / CATÁLOGO =====
  "/inventario/categorias": "catalog/categories",
  "/inventario/productos": "catalog/products",
  "/inventario/administrar": "inventory/inventories",
  "/inventario/locales": "inventory/warehouses",
  "/inventario/transferencia-de-mercaderia": "inventory/transfer",

  // ===== CLIENTES / PEOPLE =====
  "/clientes/administrar": "people/buyers",
  "/clientes/deuda-inicial": "collection/initial-debt",

  // ===== PROVEEDORES =====
  "/proveedores": "supplier/suppliers",
  "/proveedores/marcas": "supplier/brands",

  // ===== PRECIOS =====
  "/precios/administrar": "pricing/modify-price",
  "/precios/masivo": "pricing/price-bulk",
  "/precios/templates": "pricing/price-template",
  "/precios/listas": "pricing/price-list",
  "/precios/planes": "pricing/price-plan",
  "/precios/planes-mayoristas": "pricing/price-wholesaler-plan",
  "/precios/recargos": "pricing/checkout-fees",

  // ===== VENTAS / ÓRDENES =====
  "/ventas/ordenes/administrar": "order/orders",

  // ===== COBRANZAS / DEPOSITS =====
  "/cobranzas/asignar": "collection/collections",
  "/cobranzas/cobranzas": "collection/deposits",

  // ===== TESORERÍA =====
  "/tesoreria/caja": "treasury/cashdesks",
  "/tesoreria/gastos": "treasury/expenses",
  "/tesoreria/cheques": "treasury/checks",

  // ===== COMPRAS =====
  "/compras/administrar": "purchase/purchases",
  "/compras/pagos": "disbursement/disbursements",
  "/compras/pagos/asignar": "disbursement/payables",
  "/compras/compra-inicial": "purchase/initial-debt",
  "/compras/solicitud-compra": "purchase/stock-order",

  // ===== RESERVAS =====
  "/reservas/administrar": "reservation/reservations",
  "/reservas/unidad-reservable": "reservation/reservables",
  "/reservas/servicios": "reservation/services",

  // ===== CUPONES Y OFERTAS =====
  "/cupones/administrar": "coupon/coupons",
  "/ofertas/administrar": "coupon/coupons", // Ambos usan el mismo feature

  // ===== AI =====
  "/chat": "ai/chat",

  // ===== E-COMMERCE =====
  // Vendido como addon `category='module'` en trd-core. Backend script:
  // scripts/seed-ecommerce-storefront-addon.ts
  "/mi-tienda-online": "ecommerce/storefront",

  // NOTA: Ventas B2B (Big Supplier) NO se mapea a plan features.
  // El acceso se controla via whitelist (BigSupplierGuard) + permisos de empleado (PermissionsGuard).
};

/**
 * Obtiene el feature requerido para acceder a una ruta específica
 * @param pathname - Ruta del sistema (ej: "/inventario/productos")
 * @returns Feature del plan requerido o null si no hay mapeo
 */
export function getFeatureFromRoute(pathname: string): string | null {
  return ROUTE_TO_FEATURE_MAP[pathname] || null;
}

/**
 * Rutas que siempre están disponibles independientemente del plan
 */
export const ALWAYS_AVAILABLE_ROUTES = [
  "/",
  "/notificaciones",
  "owner", // Configuración - solo para owners
];

/**
 * Verifica si una ruta es de acceso libre
 * @param pathname - Ruta a verificar
 * @returns true si la ruta siempre está disponible
 */
export function isAlwaysAvailableRoute(pathname: string): boolean {
  return ALWAYS_AVAILABLE_ROUTES.includes(pathname);
}
